'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { type UseFormReturn } from 'react-hook-form'

import { type FulfillmentFormValues } from '../model/types'
import { keepDraft, keepDraftFields } from './fulfillment-draft'

/** Пауза после последней правки: пишем не на каждую букву, но задолго до ухода со страницы. */
const saveDelayMilliseconds = 800

/**
 * Что показывать в шапке шага: пока ничего не менялось — ничего. Состояние `photo-failed` значит,
 * что поля записались, а обложка нет: хранилища браузера отказывают независимо друг от друга.
 */
export type DraftSaveState = 'idle' | 'saving' | 'saved' | 'photo-failed' | 'failed'

type Params = {
  /** Автосохранение работает только в режиме добавления: ключ черновика один на оба режима. */
  isEnabled: boolean
  /** Восстановление черновика завершилось — до него писать нечего и опасно (перетрёт сохранённое). */
  isRestored: boolean
  step: number
  cover: File | null
  form: UseFormReturn<FulfillmentFormValues>
}

/**
 * Сохраняет черновик сам: через паузу после правки, а также при переходе между шагами, при смене
 * обложки и при уходе со страницы.
 *
 * Возвращает `state` — состояние записи для надписи в шапке шага — и `cancel()`. Последний зовут
 * там, где черновик больше не нужен: он выключает сохранение навсегда и ждёт уже начатую запись,
 * иначе она воскресила бы черновик уже после создания карточки. Отсюда и его асинхронность.
 *
 * Пишет в хранилища браузера: поля — в localStorage, обложку — в IndexedDB.
 */
export const useDraftAutosave = ({ isEnabled, isRestored, step, cover, form }: Params) => {
  const [state, setState] = useState<DraftSaveState>('idle')
  const timer = useRef<number | null>(null)
  const isStopped = useRef(false)
  /** Номер шага и обложка нужны отложенной записи, а пересоздавать из-за них подписку незачем. */
  const currentStep = useRef(step)
  const currentCover = useRef(cover)
  /**
   * Обложка тяжёлая, поэтому пишется не по расписанию, а когда сменился сам файл. `undefined` —
   * «ещё не следим»: до первой готовности обложка в форме пришла из черновика и уже сохранена.
   */
  const keptCover = useRef<File | null | undefined>(undefined)
  /** Обложка ждёт записи: правка поля не должна отменить её, перезаписав отложенное действие. */
  const isCoverPending = useRef(false)
  /**
   * Легла ли текущая обложка в хранилище. Запись одних полей не делает потерянное фото
   * сохранённым, поэтому статус берётся отсюда, а не из результата последней записи.
   */
  const isCoverKept = useRef(true)
  /** Начатая запись: остановка ждёт её, иначе запоздавшая обложка легла бы уже после очистки. */
  const currentWrite = useRef<Promise<void>>(Promise.resolve())

  const isReady = isEnabled && isRestored

  const stopTimer = () => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current)
      timer.current = null
    }
  }

  /**
   * Дописать поля немедленно. Годится только localStorage: он пишет мгновенно, а обложку в
   * IndexedDB браузер в такие моменты уже не ждёт. Зовётся там, где форма исчезает раньше
   * отложенной записи, — при уходе со страницы и при переходе по ссылке внутри сайта.
   */
  const saveFieldsNow = useCallback(() => {
    if (!isStopped.current && form.formState.isDirty) {
      keepDraftFields(form.getValues(), currentStep.current)
    }
  }, [form])

  /**
   * Больше не сохранять. Ждёт уже начатую запись: она пишет обложку в IndexedDB и без ожидания
   * успела бы положить файл обратно после очистки черновика — тот остался бы невидимым мусором
   * и всплыл бы в следующей карточке.
   */
  const cancel = useCallback(async () => {
    isStopped.current = true
    stopTimer()
    setState('idle')
    await currentWrite.current
  }, [])

  /**
   * Откладывает запись. Нулевая задержка значит «сразу после текущей отрисовки», а не «прямо
   * сейчас»: запись всегда уходит из таймера, чтобы не менять состояние во время отрисовки формы.
   */
  const scheduleSave = useCallback(
    (delayMilliseconds: number, withCover = false) => {
      if (isStopped.current) {
        return
      }
      if (withCover) {
        isCoverPending.current = true
      }
      stopTimer()
      timer.current = window.setTimeout(() => {
        const includesCover = isCoverPending.current
        isCoverPending.current = false
        currentWrite.current = (async () => {
          const values = form.getValues()
          let areFieldsKept: boolean
          if (includesCover) {
            const kept = await keepDraft(values, currentStep.current, currentCover.current)
            areFieldsKept = kept.areFieldsKept
            isCoverKept.current = kept.isPhotoKept
          } else {
            areFieldsKept = keepDraftFields(values, currentStep.current)
          }
          if (isStopped.current) {
            return
          }
          if (!areFieldsKept) {
            setState('failed')

            return
          }
          // Не дошедшую обложку черновик уже убрал у себя, и молчать об этом нельзя: иначе человек
          // уйдёт со страницы уверенным, что фото сохранено вместе с полями.
          setState(isCoverKept.current ? 'saved' : 'photo-failed')
        })()
      }, delayMilliseconds)
    },
    [form]
  )

  useEffect(() => {
    if (!isReady) {
      return
    }
    const unsubscribe = form.subscribe({
      formState: { values: true, isDirty: true },
      callback: ({ isDirty }) => {
        // Пока человек ничего не менял, черновика быть не должно: иначе он заведётся у любого,
        // кто просто открыл страницу, и потом «восстановится» пустым.
        if (!isDirty) {
          return
        }
        setState('saving')
        scheduleSave(saveDelayMilliseconds)
      },
    })

    return () => {
      unsubscribe()
      // Переход по ссылке внутри сайта убирает форму с экрана раньше, чем сработает отложенная
      // запись, а `pagehide` при такой навигации не наступает — правки последней секунды пропали
      // бы молча.
      if (timer.current !== null) {
        saveFieldsNow()
      }
      stopTimer()
    }
  }, [form, isReady, saveFieldsNow, scheduleSave])

  // Уход со страницы обрывает отложенную запись, поэтому дописываем поля сразу.
  useEffect(() => {
    if (!isReady) {
      return
    }
    window.addEventListener('pagehide', saveFieldsNow)

    return () => window.removeEventListener('pagehide', saveFieldsNow)
  }, [isReady, saveFieldsNow])

  // Переход между шагами — хороший момент записать: человек закончил порцию работы.
  useEffect(() => {
    currentStep.current = step
    if (!isReady || !form.formState.isDirty) {
      return
    }
    scheduleSave(0)
  }, [form, isReady, scheduleSave, step])

  useEffect(() => {
    currentCover.current = cover
    if (!isReady) {
      return
    }
    const previousCover = keptCover.current
    keptCover.current = cover
    if (previousCover === undefined || cover === previousCover) {
      return
    }
    scheduleSave(0, true)
  }, [cover, isReady, scheduleSave])

  return { state, cancel }
}
